import { Injectable } from '@angular/core';
import { FilterCriteria } from '../../models/api/reports/filter-detail-response';
import { ScheduledDateMapping } from '../../models/api/reports/report';
import { ProcessedControl } from './reports.service';

export interface IncrementOption {
  name: string;
  displayName: string;
  fromControl: string;
  toControl: string;
  incrementFrom: string;
  incrementTo: string;
  disabled: boolean;
  fromValue?: string;
  toValue?: string;
}

export interface IncrementMapping {
  displayName: string;
  fromControls: string[];
  toControls: string[];
  incrementFrom: string;
  incrementTo: string;
}

export const INCREMENT_MAPPINGS: IncrementMapping[] = [
  {
    displayName: 'Loss Date',
    fromControls: ['claim.DATE_OF_LOSS.FROM'],
    toControls: ['claim.DATE_OF_LOSS.TO'],
    incrementFrom: 'INCREMENT_CLAIM_LOSSDATE_FROM',
    incrementTo: 'INCREMENT_CLAIM_LOSSDATE_TO',
  },
  {
    displayName: 'Reported to Client',
    fromControls: ['claim.DATE_REPORTED_TO_CLIENT.FROM'],
    toControls: ['claim.DATE_REPORTED_TO_CLIENT.TO'],
    incrementFrom: 'INCREMENT_CLAIM_REPORTEDTOCLIENT_FROM',
    incrementTo: 'INCREMENT_CLAIM_REPORTEDTOCLIENT_TO',
  },
  {
    displayName: 'Reported to Sedgwick',
    fromControls: ['claim.DATE_REPORTED_TO_CMS.FROM'],
    toControls: ['claim.DATE_REPORTED_TO_CMS.TO'],
    incrementFrom: 'INCREMENT_CLAIM_REPORTEDTOCMS_FROM',
    incrementTo: 'INCREMENT_CLAIM_REPORTEDTOCMS_TO',
  },
  {
    displayName: 'Claim Opened',
    fromControls: ['claim.DATE_CLAIM_OPENED.FROM'],
    toControls: ['claim.DATE_CLAIM_OPENED.TO'],
    incrementFrom: 'INCREMENT_CLAIM_OPENED_FROM',
    incrementTo: 'INCREMENT_CLAIM_OPENED_TO',
  },
  {
    displayName: 'Claim Closed',
    fromControls: ['filter_close_date.FILTER_DATE_CLOSE.FROM'],
    toControls: ['filter_close_date.FILTER_DATE_CLOSE.TO'],
    incrementFrom: 'INCREMENT_CLAIM_CLOSED_FROM',
    incrementTo: 'INCREMENT_CLAIM_CLOSED_TO',
  },
  {
    displayName: 'Claim Reopened',
    fromControls: ['filter_reopen_date.FILTER_DATE_REOPEN.FROM'],
    toControls: ['filter_reopen_date.FILTER_DATE_REOPEN.TO'],
    incrementFrom: 'INCREMENT_CLAIM_REOPENED_FROM',
    incrementTo: 'INCREMENT_CLAIM_REOPENED_TO',
  },
  {
    displayName: 'Last Update',
    fromControls: ['claim.DATE_UPDATED.FROM'],
    toControls: ['claim.DATE_UPDATED.TO'],
    incrementFrom: 'INCREMENT_CLAIM_LASTUPDATE_FROM',
    incrementTo: 'INCREMENT_CLAIM_LASTUPDATE_TO',
  },
  {
    displayName: 'Last Financial Update',
    fromControls: ['claim.DATE_FINANCIALS_UPDATED.FROM'],
    toControls: ['claim.DATE_FINANCIALS_UPDATED.TO'],
    incrementFrom: 'INCREMENT_CLAIM_LASTFINANCIALUPDATE_FROM',
    incrementTo: 'INCREMENT_CLAIM_LASTFINANCIALUPDATE_TO',
  },
  {
    displayName: 'Activity Date',
    fromControls: ['TRANSACTION_FROM_DATE'],
    toControls: ['TRANSACTION_TO_DATE'],
    incrementFrom: 'INCREMENT_TRANSACTION_FROM',
    incrementTo: 'INCREMENT_TRANSACTION_TO',
  },
  {
    displayName: 'Off Work Work Status Effective',
    fromControls: ['work_status_as_of_4.DATE_OFF_WORK_WORK_STATUS.FROM'],
    toControls: ['work_status_as_of_4.DATE_OFF_WORK_WORK_STATUS.TO'],
    incrementFrom: 'INCREMENT_OFF_WORK_WORK_STATUS_FROM',
    incrementTo: 'INCREMENT_OFF_WORK_WORK_STATUS_TO',
  },
  {
    displayName: 'Normal Work Status Effective',
    fromControls: ['work_status_as_of_4.DATE_NORMAL_WORK_STATUS.FROM'],
    toControls: ['work_status_as_of_4.DATE_NORMAL_WORK_STATUS.TO'],
    incrementFrom: 'INCREMENT_NORMAL_WORK_STATUS_FROM',
    incrementTo: 'INCREMENT_NORMAL_WORK_STATUS_TO',
  },
  {
    displayName: 'Restricted Work Status Effective',
    fromControls: ['work_status_as_of_4.DATE_RESTRICTED_WORK_STATUS.FROM'],
    toControls: ['work_status_as_of_4.DATE_RESTRICTED_WORK_STATUS.TO'],
    incrementFrom: 'INCREMENT_RESTRICTED_WORK_STATUS_FROM',
    incrementTo: 'INCREMENT_RESTRICTED_WORK_STATUS_TO',
  },
  {
    displayName: 'Restricted-NTL Work Status Effective',
    fromControls: ['work_status_as_of_4.DATE_RESTRICTED_NTL_WORK_STATUS.FROM'],
    toControls: ['work_status_as_of_4.DATE_RESTRICTED_NTL_WORK_STATUS.TO'],
    incrementFrom: 'INCREMENT_RESTRICTED_NTL_WORK_STATUS_FROM',
    incrementTo: 'INCREMENT_RESTRICTED_NTL_WORK_STATUS_TO',
  },
  {
    displayName: 'Terminated Work Status Effective',
    fromControls: ['work_status_as_of_4.DATE_TERMINATED_WORK_STATUS.FROM'],
    toControls: ['work_status_as_of_4.DATE_TERMINATED_WORK_STATUS.TO'],
    incrementFrom: 'INCREMENT_TERMINATED_WORK_STATUS_FROM',
    incrementTo: 'INCREMENT_TERMINATED_WORK_STATUS_TO',
  },
];

export const AS_OF_DATE_MAPPING: IncrementMapping = {
  displayName: 'as of date',
  fromControls: ['AS_OF_DATE'],
  toControls: [],
  incrementFrom: 'INCREMENT_AS_OF_TO',
  incrementTo: '',
};

export const COMPARED_WITH_DATE_MAPPING: IncrementMapping = {
  displayName: 'comparative date',
  fromControls: ['COMPARED_WITH_AS_OF_DATE'],
  toControls: [],
  incrementFrom: 'INCREMENT_COMPARED_WITH',
  incrementTo: '',
};

export const ACCESS_FROM_DATE_MAPPING: IncrementMapping = {
  displayName: 'access from date',
  fromControls: ['TRANSACTION_FROM_DATE'],
  toControls: [],
  incrementFrom: 'INCREMENT_TRANSACTION_FROM',
  incrementTo: '',
};

export const ACCESS_TO_DATE_MAPPING: IncrementMapping = {
  displayName: 'access to date',
  fromControls: ['TRANSACTION_TO_DATE'],
  toControls: [],
  incrementFrom: 'INCREMENT_TRANSACTION_TO',
  incrementTo: '',
};

export const SCHEDULE_OPTION_MAPPINGS: IncrementMapping[] = [
  AS_OF_DATE_MAPPING,
  COMPARED_WITH_DATE_MAPPING,
];

export const ACCESS_DATE_SCHEDULE_MAPPINGS: IncrementMapping[] = [
  ACCESS_FROM_DATE_MAPPING,
  ACCESS_TO_DATE_MAPPING,
];

@Injectable({ providedIn: 'root' })
export class IncrementOptionsService {
  private readonly columnDisplayNames = new Map<string, string>(
    INCREMENT_MAPPINGS.flatMap((m) =>
      m.fromControls
        .map((ctrl) => this.extractColumnName(ctrl))
        .filter((col): col is string => col !== null)
        .map((col) => [col, m.displayName] as [string, string]),
    ),
  );

  private readonly dateFields = new Set<string>(this.columnDisplayNames.keys());

  private readonly dateColumnDisplayNames = new Map<string, string>([
    ...INCREMENT_MAPPINGS.flatMap((m) => [
      ...m.fromControls.map(
        (ctrl) =>
          [ctrl.replace('.FROM', ''), m.displayName] as [string, string],
      ),
      ...m.toControls.map(
        (ctrl) => [ctrl.replace('.TO', ''), m.displayName] as [string, string],
      ),
    ]),
    ['TRANSACTION_FROM_DATE', 'Activity Date'],
    ['TRANSACTION_TO_DATE', 'Activity Date'],
    ['AS_OF_DATE', 'As Of Date'],
  ]);

  private extractColumnName(control: string): string | null {
    const parts = control.split('.');
    return parts.length >= 2
      ? parts[1].replace('.FROM', '').replace('.TO', '')
      : null;
  }

  getOptions(
    type: 'FROM' | 'TO',
    controlsMap: Map<string, ProcessedControl> | undefined,
    scheduledDateMappings?: ScheduledDateMapping[],
    filterCriteria?: FilterCriteria[],
  ): IncrementOption[] {
    return this.deduplicateOptions([
      this.buildFromScheduledMappings(scheduledDateMappings, type, controlsMap),
      this.buildFromFilterCriteria(filterCriteria, type),
      this.buildFromControlsMap(controlsMap, type),
    ]);
  }

  private deduplicateOptions(
    optionGroups: IncrementOption[][],
  ): IncrementOption[] {
    const seen = new Set<string>();
    return optionGroups.flat().filter((opt) => {
      if (seen.has(opt.displayName)) return false;
      seen.add(opt.displayName);
      return true;
    });
  }

  getAllOptions(
    type: 'FROM' | 'TO',
    controlsMap: Map<string, ProcessedControl> | undefined,
    scheduledDateMappings?: ScheduledDateMapping[],
    filterCriteria?: FilterCriteria[],
  ): IncrementOption[] {
    const enabledOptions = this.getOptions(
      type,
      controlsMap,
      scheduledDateMappings,
      filterCriteria,
    );
    const enabledByName = new Map(
      enabledOptions.map((o) => [o.displayName, o]),
    );

    const transactionLabel = this.resolveTransactionLabel(controlsMap, type);

    return INCREMENT_MAPPINGS.map((mapping) => {
      const enabled = enabledByName.get(mapping.displayName);
      const isTransaction = mapping.displayName === 'Activity Date';
      const label = isTransaction ? transactionLabel : mapping.displayName;

      return {
        name: enabled?.name || label,
        displayName: mapping.displayName,
        fromControl: type === 'FROM' ? enabled?.fromControl || '' : '',
        toControl: type === 'TO' ? enabled?.toControl || '' : '',
        incrementFrom: mapping.incrementFrom,
        incrementTo: mapping.incrementTo,
        disabled: !enabled,
        fromValue: type === 'FROM' ? enabled?.fromValue : undefined,
        toValue: type === 'TO' ? enabled?.toValue : undefined,
      };
    });
  }

  getScheduleOptions(
    controlsMap: Map<string, ProcessedControl> | undefined,
    scheduledDateMappings?: ScheduledDateMapping[],
    hasFilter = false,
    hasIncrementFromTo = true,
  ): IncrementOption[] {
    const mappings = hasIncrementFromTo
      ? SCHEDULE_OPTION_MAPPINGS
      : [...SCHEDULE_OPTION_MAPPINGS, ...ACCESS_DATE_SCHEDULE_MAPPINGS];

    const options: IncrementOption[] = [];

    for (const mapping of mappings) {
      const option = this.resolveScheduleOption(
        mapping,
        controlsMap,
        scheduledDateMappings,
        hasFilter,
      );
      if (option) {
        options.push(option);
      }
    }

    return options;
  }

  private resolveScheduleOption(
    mapping: IncrementMapping,
    controlsMap: Map<string, ProcessedControl> | undefined,
    scheduledDateMappings?: ScheduledDateMapping[],
    hasFilter = false,
  ): IncrementOption | null {
    if (hasFilter && scheduledDateMappings?.length) {
      const sdm = scheduledDateMappings.find((m) =>
        mapping.fromControls.includes(m.dateColumnId),
      );
      if (sdm) {
        return this.createScheduleOption(mapping, sdm.currentDateValue, false);
      }
    }

    const control = this.findControlWithValue(
      controlsMap,
      mapping.fromControls,
    );
    if (control) {
      return this.createScheduleOption(
        mapping,
        control.defaultValue,
        false,
        control.label,
      );
    }

    return null;
  }

  private createScheduleOption(
    mapping: IncrementMapping,
    fromValue: string | undefined,
    disabled: boolean,
    controlLabel?: string,
  ): IncrementOption {
    const dateValue = this.formatDateValue(fromValue);
    const label = controlLabel
      ? `Increment ${controlLabel.toLowerCase()}`
      : `Increment ${mapping.displayName}`;
    return {
      name: dateValue ? `${label} (${dateValue})` : label,
      displayName: mapping.displayName,
      fromControl: mapping.fromControls[0] || '',
      toControl: '',
      incrementFrom: mapping.incrementFrom,
      incrementTo: mapping.incrementTo,
      disabled,
      fromValue,
    };
  }

  private buildFromScheduledMappings(
    mappings: ScheduledDateMapping[] | undefined,
    type: 'FROM' | 'TO',
    controlsMap?: Map<string, ProcessedControl>,
  ): IncrementOption[] {
    if (!mappings?.length) return [];

    const transactionLabel = this.resolveTransactionLabel(controlsMap, type);

    return mappings
      .filter((m) => m.srdColumnType === type)
      .map((m) => {
        const isTransaction =
          m.dateColumnId === 'TRANSACTION_FROM_DATE' ||
          m.dateColumnId === 'TRANSACTION_TO_DATE';
        const label = isTransaction
          ? transactionLabel
          : this.dateColumnDisplayNames.get(m.dateColumnId) || m.dateColumnId;

        return this.createOption(
          label,
          type,
          m.dateColumnId,
          m.currentDateValue,
        );
      });
  }

  private buildFromFilterCriteria(
    criteria: FilterCriteria[] | undefined,
    type: 'FROM' | 'TO',
  ): IncrementOption[] {
    if (!criteria?.length) return [];

    return criteria
      .filter((c) => c.operator === 'BETWEEN' && this.dateFields.has(c.column))
      .map((c) => {
        const dates = c.values[0]?.split('|') || [];
        const dateValue = type === 'FROM' ? dates[0] : dates[1];
        return { column: c.column, dateValue };
      })
      .filter((c) => c.dateValue)
      .map((c) =>
        this.createOption(
          this.columnDisplayNames.get(c.column) || c.column,
          type,
          c.column,
          c.dateValue,
        ),
      );
  }

  private buildFromControlsMap(
    controlsMap: Map<string, ProcessedControl> | undefined,
    type: 'FROM' | 'TO',
  ): IncrementOption[] {
    if (!controlsMap) return [];

    const controlKey = type === 'FROM' ? 'fromControls' : 'toControls';
    return INCREMENT_MAPPINGS.map((m) => ({
      mapping: m,
      control: this.findControlWithValue(controlsMap, m[controlKey]),
    }))
      .filter((item) => item.control)
      .map(({ mapping, control }) => {
        const label = control!.label
          ? this.stripFromTo(control!.label)
          : mapping.displayName;
        return {
          ...this.createOption(
            label,
            type,
            control!.erfName,
            control!.defaultValue,
          ),
          displayName: mapping.displayName,
          incrementFrom: mapping.incrementFrom,
          incrementTo: mapping.incrementTo,
        };
      });
  }

  private createOption(
    displayName: string,
    type: 'FROM' | 'TO',
    controlId: string,
    dateValue: string | undefined,
  ): IncrementOption {
    const formatted = this.formatDateValue(dateValue);
    const mapping = INCREMENT_MAPPINGS.find(
      (m) => m.displayName === displayName,
    );
    return {
      name: formatted ? `${displayName} (${formatted})` : displayName,
      displayName,
      fromControl: type === 'FROM' ? controlId : '',
      toControl: type === 'TO' ? controlId : '',
      incrementFrom: mapping?.incrementFrom || '',
      incrementTo: mapping?.incrementTo || '',
      disabled: false,
      fromValue: type === 'FROM' ? dateValue : undefined,
      toValue: type === 'TO' ? dateValue : undefined,
    };
  }

  private resolveTransactionLabel(
    controlsMap: Map<string, ProcessedControl> | undefined,
    type: 'FROM' | 'TO',
  ): string {
    const erfName =
      type === 'FROM' ? 'TRANSACTION_FROM_DATE' : 'TRANSACTION_TO_DATE';
    const control = controlsMap?.get(erfName);
    if (control?.label) {
      return this.stripFromTo(control.label);
    }
    return 'Activity Date';
  }

  private stripFromTo(label: string): string {
    return label
      .replace(/\bFrom\b/i, '')
      .replace(/\bTo\b/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private findControlWithValue(
    controlsMap: Map<string, ProcessedControl> | undefined,
    erfNames: string[],
  ): ProcessedControl | undefined {
    if (!controlsMap) return undefined;
    return erfNames.map((n) => controlsMap.get(n)).find((c) => c?.defaultValue);
  }

  private formatDateValue(value: string | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getMonth() + 1)}-${pad(
      date.getDate(),
    )}-${date.getFullYear()}`;
  }

  getDisplayToErfMap(): Record<string, { from: string; to: string }> {
    const result: Record<string, { from: string; to: string }> = {};
    for (const m of INCREMENT_MAPPINGS) {
      result[m.displayName] = { from: m.incrementFrom, to: m.incrementTo };
    }
    for (const m of [
      ...SCHEDULE_OPTION_MAPPINGS,
      ...ACCESS_DATE_SCHEDULE_MAPPINGS,
    ]) {
      result[m.displayName] = {
        from: m.incrementFrom,
        to: m.incrementTo,
      };
    }
    return result;
  }

  getIncrementErfMapping(): Map<string, { from: string; to: string }> {
    return new Map(Object.entries(this.getDisplayToErfMap()));
  }

  getErfToDisplayMapping(hasIncrementFromTo = true): Record<string, string> {
    const schedMappings = hasIncrementFromTo
      ? SCHEDULE_OPTION_MAPPINGS
      : [...SCHEDULE_OPTION_MAPPINGS, ...ACCESS_DATE_SCHEDULE_MAPPINGS];

    const result: Record<string, string> = {};
    for (const m of [...INCREMENT_MAPPINGS, ...schedMappings]) {
      if (m.incrementFrom) result[m.incrementFrom] = m.displayName;
      if (m.incrementTo) result[m.incrementTo] = m.displayName;
    }
    return result;
  }

  getAllIncrementErfNames(): string[] {
    return [
      ...INCREMENT_MAPPINGS.flatMap((m) => [m.incrementFrom, m.incrementTo]),
      ...[...SCHEDULE_OPTION_MAPPINGS, ...ACCESS_DATE_SCHEDULE_MAPPINGS].map(
        (m) => m.incrementFrom,
      ),
    ].filter(Boolean);
  }

  getScheduleOptionErfNames(hasIncrementFromTo = true): Set<string> {
    const mappings = hasIncrementFromTo
      ? SCHEDULE_OPTION_MAPPINGS
      : [...SCHEDULE_OPTION_MAPPINGS, ...ACCESS_DATE_SCHEDULE_MAPPINGS];

    return new Set(mappings.map((m) => m.incrementFrom).filter(Boolean));
  }
}
